import { Controller } from "@hotwired/stimulus";
import { destroy } from "@rails/request.js";

// Checkbox selection is UI-only — it doesn't create/destroy any
// OrderItemPhoto join. It drives:
//   - Shift-click range select/deselect via handlePhotoClick
//   - deleteFiles: delete selected photos from the library (server-side
//     guard still blocks photos used in any order_item)
//   - syncSelectionBar: updates the "Označené: N" counter and the master
//     checkbox tri-state (bar visibility is managed by photo-upload)
//   - toggleAll: master checkbox selects/deselects every photo
// Bulk quantity edits go through the bulk-quantity controller; per-photo
// +/- only affects the photo whose stepper was clicked.
export default class extends Controller {
  static targets = ["photo", "bar", "count", "master", "masterLabel", "mobileSelectionBar"];

  // Wording comes from the view so it follows the domain's language.
  static values = { labels: Object };

  connect() {
    this.lastCheckedPhoto = null;
    this.syncSelectionBar();
  }

  photoTargetConnected() {
    this.syncSelectionBar();
  }

  photoTargetDisconnected() {
    this.syncSelectionBar();
  }

  handlePhotoClick(event) {
    const current = event.target;

    if (!event.shiftKey || !this.lastCheckedPhoto) {
      this.lastCheckedPhoto = current;
      this.syncRowSelection(current, current.checked);
      this.dispatch("selectionChanged", { bubbles: true, prefix: "photo-selection" });
      this.syncSelectionBar();
      return;
    }

    const photos = this.photoTargets;
    const start = photos.indexOf(this.lastCheckedPhoto);
    const end = photos.indexOf(current);

    if (start === -1 || end === -1) {
      this.lastCheckedPhoto = current;
      this.syncRowSelection(current, current.checked);
      this.dispatch("selectionChanged", { bubbles: true, prefix: "photo-selection" });
      this.syncSelectionBar();
      return;
    }

    const [from, to] = start < end ? [start, end] : [end, start];
    const shouldCheck = current.checked;

    for (let i = from; i <= to; i += 1) {
      photos[i].checked = shouldCheck;
      this.syncRowSelection(photos[i], shouldCheck);
    }
    this.lastCheckedPhoto = current;
    this.dispatch("selectionChanged", { bubbles: true, prefix: "photo-selection" });
    this.syncSelectionBar();
  }

  toggleAll() {
    const total = this.photoTargets.length;
    if (total === 0) return;

    const allChecked = this.photoTargets.every((cb) => cb.checked);
    const shouldCheck = !allChecked;
    this.photoTargets.forEach((cb) => {
      cb.checked = shouldCheck;
      this.syncRowSelection(cb, shouldCheck);
    });
    this.dispatch("selectionChanged", { bubbles: true, prefix: "photo-selection" });
    this.syncSelectionBar();
  }

  // When a photo becomes selected, seed its quantity to 1 if still 0 —
  // mirrors the legacy default where uploaded photos got OrderItemPhoto
  // records with quantity=1 eagerly. Existing non-zero quantities are
  // preserved (re-selecting a previously adjusted photo keeps its value).
  syncRowSelection(checkbox, selected) {
    const row = checkbox.closest("[data-configurator-state-target='photoRow']");
    if (!row) return;
    row.dataset.selected = selected ? "true" : "false";
    if (selected && Number(row.dataset.quantity) === 0) {
      row.dataset.quantity = "1";
      const input = row.querySelector("[data-photo-quantity-target='input']");
      if (input) input.value = "1";
    }
  }

  deleteFiles() {
    const checked = this.photoTargets.filter((photo) => photo.checked);
    checked.forEach((photo) => {
      destroy(photo.dataset.url, {
        responseKind: "turbo-stream",
      });
    });
  }

  syncSelectionBar() {
    const total = this.photoTargets.length;
    const checked = this.photoTargets.filter((cb) => cb.checked).length;

    if (this.hasCountTarget) {
      this.countTarget.textContent = checked;
    }

    if (this.hasMasterTarget) {
      this.masterTarget.disabled = total === 0;
    }

    if (this.hasMasterLabelTarget) {
      const allChecked = total > 0 && checked === total;
      this.masterLabelTarget.textContent = allChecked
        ? this.labelsValue.deselect_all
        : this.labelsValue.select_all;
    }

    // Mobile: selection bar appears ABOVE the always-visible submit bar when at
    // least one photo is selected. Submit is never hidden — uploads auto-select
    // photos with quantity 1, so a swap pattern would hide the primary CTA the
    // moment a user uploads anything.
    if (this.hasMobileSelectionBarTarget) {
      this.mobileSelectionBarTarget.classList.toggle("hidden", checked === 0);
    }
  }
}
